const instances = new WeakMap();

function setText(element, value) {
  if (element && element.textContent !== String(value)) element.textContent = String(value);
}

function message(template, values = {}) {
  return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}|__([A-Z]+)__/g, (match, liquidKey, tokenKey) => {
    const key = liquidKey || tokenKey.toLowerCase();
    return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
  });
}

function initialize(root) {
  if (instances.has(root)) return;
  let destroyed = false;
  let timer = null;
  let calendar = null;
  let config;
  try {
    config = JSON.parse(root.querySelector('[data-tracking-config]').textContent);
  } catch {
    return;
  }
  const messages = config.messages || {};
  const orders = Array.from(root.querySelectorAll('[data-courum-order]')).map(element => {
    try {
      return { element, data: JSON.parse(element.querySelector('[data-order-data]').textContent), buttons: Array.from(element.querySelectorAll('[data-stage-index]')), explored: null };
    } catch {
      return null;
    }
  }).filter(Boolean);
  const form = root.querySelector('[data-tracking-login]');

  function submit(event) {
    if (!form.reportValidity()) return;
    event.preventDefault();
    const email = form.querySelector('input[type="email"]').value.trim();
    const url = new URL(form.dataset.loginUrl || form.action, window.location.origin);
    url.searchParams.set('login_hint', email);
    if (form.dataset.designMode === 'true') window.open(url.href, '_blank', 'noopener,noreferrer');
    else window.location.assign(url.href);
  }

  function showDetail(order, index) {
    const button = order.buttons[index];
    if (!button) return;
    setText(order.element.querySelector('[data-detail-title]'), button.dataset.stageTitle);
    setText(order.element.querySelector('[data-detail-description]'), button.dataset.stageDescription);
    order.buttons.forEach((item, itemIndex) => {
      item.classList.toggle('is-explored', itemIndex === index);
      item.setAttribute('aria-pressed', String(itemIndex === index));
    });
  }

  function click(event) {
    const button = event.target.closest('[data-stage-index]');
    if (!button || !root.contains(button)) return;
    const order = orders.find(item => item.element.contains(button));
    if (!order) return;
    order.explored = Number(button.dataset.stageIndex);
    showDetail(order, order.explored);
  }

  function latestDate(values, now, excludeFuture = false) {
    return (values || []).map(value => calendar.parseTrackingDate(value, config.timeZone)).filter(value => value && (!excludeFuture || value <= now)).sort((a, b) => b - a)[0] || null;
  }

  function updateOrder(order, now) {
    const input = { ...order.data, now, timeZone: config.timeZone, holidays: config.holidays };
    const candidates = order.data.tagCandidates || {};
    input.transportAt = latestDate(candidates.transport || [input.transportAt], now, true);
    input.pauseAt = latestDate(candidates.pause || [input.pauseAt], now);
    input.stopAt = latestDate(candidates.stop || [input.stopAt], now);
    if (config.estimatesEnabled === false) input.paid = false;
    const progress = calendar.calculateTrackingProgress(input);
    const index = progress.stageIndex;
    const stageTitle = order.buttons[index].dataset.stageTitle;
    const final = progress.finalState;
    const isTerminal = final && final !== 'paused';
    const title = isTerminal ? messages[`${final}_title`] : stageTitle;
    const label = isTerminal ? messages.terminal_label : progress.estimated ? messages.estimated_label : messages.confirmed_label;
    setText(order.element.querySelector('[data-current-title]'), title);
    setText(order.element.querySelector('[data-current-label]'), label);
    setText(order.element.querySelector('[data-current-index]'), String(index + 1).padStart(2, '0'));
    const fill = order.element.querySelector('[data-progress]');
    const width = `${index / 6 * 100}%`;
    if (fill.style.width !== width) fill.style.width = width;
    const bar = fill.parentElement;
    bar.setAttribute('aria-valuenow', String(index));
    bar.setAttribute('aria-valuetext', message(progress.estimated ? messages.estimated_accessible : messages.confirmed_accessible, { number: String(index + 1).padStart(2, '0'), stage: title }));
    order.buttons.forEach((button, buttonIndex) => {
      button.classList.toggle('is-current', buttonIndex === index);
      button.classList.toggle('is-complete', buttonIndex < index);
      button.classList.toggle('is-pending', buttonIndex > index);
      if (buttonIndex === index) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });
    if (order.explored === null) {
      showDetail(order, index);
      if (isTerminal) {
        setText(order.element.querySelector('[data-detail-title]'), title);
        setText(order.element.querySelector('[data-detail-description]'), messages[`${final}_body`]);
      }
    }
    setText(order.element.querySelector('[data-summary-label]'), progress.estimated ? messages.estimated_label : messages.summary_stage);
    setText(order.element.querySelector('[data-summary-stage]'), title);
    const note = order.element.querySelector('[data-confirmation-note]');
    note.hidden = !progress.estimated;
    setText(note, message(messages.estimated_note, { stage: order.buttons[progress.confirmedStage].dataset.stageTitle }));
    const stateDescription = order.element.querySelector('[data-state-description]');
    stateDescription.hidden = !final;
    setText(stateDescription, final ? `${final === 'paused' ? `${messages.paused_title}. ` : ''}${messages[`${final}_body`] || ''}` : '');
    const next = order.element.querySelector('[data-next-update]');
    next.hidden = Boolean(final);
    let nextText = messages.check_again;
    if (progress.nextDate) {
      const date = new Intl.DateTimeFormat(config.locale || 'pt-BR', { timeZone: config.timeZone, day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(progress.nextDate));
      nextText = message(messages.next_update, { date });
      if (input.imported && index === 6) nextText += ` ${messages.international_note}`;
    } else if (index === 5 && !final) nextText = messages.preparation_complete;
    setText(next, nextText);
    order.element.querySelector('[data-fallback]').hidden = true;
    return progress.nextDate ? new Date(progress.nextDate).getTime() : null;
  }

  function refresh() {
    clearTimeout(timer);
    timer = null;
    if (destroyed || !calendar || document.hidden) return;
    const now = new Date();
    let delay = 60000;
    for (const order of orders) {
      try {
        const next = updateOrder(order, now);
        if (next && next > now.getTime()) delay = Math.min(delay, Math.max(1, next - now.getTime()));
      } catch {
        order.element.querySelector('[data-fallback]').hidden = false;
      }
    }
    if (orders.length) timer = window.setTimeout(refresh, delay);
  }

  function visibility() {
    if (document.hidden) {
      clearTimeout(timer);
      timer = null;
    } else refresh();
  }

  function destroy() {
    destroyed = true;
    clearTimeout(timer);
    root.removeEventListener('click', click);
    if (form) form.removeEventListener('submit', submit);
    document.removeEventListener('visibilitychange', visibility);
    window.removeEventListener('pageshow', refresh);
    instances.delete(root);
  }

  instances.set(root, { destroy });
  root.addEventListener('click', click);
  if (form) form.addEventListener('submit', submit);
  if (!orders.length) return;
  document.addEventListener('visibilitychange', visibility);
  window.addEventListener('pageshow', refresh);
  import(root.dataset.calendarUrl).then(module => {
    if (destroyed) return;
    calendar = module;
    refresh();
  }).catch(() => {
    if (!destroyed) orders.forEach(order => { order.element.querySelector('[data-fallback]').hidden = false; });
  });
}

function initializeWithin(target) {
  if (target.matches?.('[data-courum-tracking]')) initialize(target);
  target.querySelectorAll('[data-courum-tracking]').forEach(initialize);
}

initializeWithin(document);
document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
document.addEventListener('shopify:section:unload', event => {
  const roots = event.target.matches?.('[data-courum-tracking]') ? [event.target] : event.target.querySelectorAll('[data-courum-tracking]');
  roots.forEach(root => instances.get(root)?.destroy());
});

